import { Linking } from 'react-native';

import { SECURE_STORE_KEYS } from '@/constants/api';
import { secureStore } from '@/lib/secureStore';
import { REFERRAL_LEGACY_REF_ID_PARAM, REFERRAL_TOKEN_PARAM } from '@/lib/shareLinks';

export type PendingReferral = {
  refId?: string;
  referralLinkToken?: string;
};

export function extractPendingReferral(url: string | null | undefined): PendingReferral | null {
  if (!url) return null;

  const params = queryParamsFor(url);
  const referralLinkToken = cleanParam(params.get(REFERRAL_TOKEN_PARAM));
  if (referralLinkToken) return { referralLinkToken };

  const refId = cleanParam(params.get(REFERRAL_LEGACY_REF_ID_PARAM));
  return refId ? { refId } : null;
}

export function serializePendingReferral(referral: PendingReferral): string | null {
  if (referral.referralLinkToken) {
    return `${REFERRAL_TOKEN_PARAM}=${encodeURIComponent(referral.referralLinkToken)}`;
  }
  if (referral.refId) {
    return `${REFERRAL_LEGACY_REF_ID_PARAM}=${encodeURIComponent(referral.refId)}`;
  }
  return null;
}

export function parseStoredPendingReferral(value: string | null): PendingReferral | null {
  if (!value) return null;
  return extractPendingReferral(`marketpryce://pending?${value}`);
}

export async function captureReferralFromUrl(url: string | null | undefined): Promise<boolean> {
  const referral = extractPendingReferral(url);
  const serialized = referral && serializePendingReferral(referral);
  if (!serialized) return false;

  await secureStore.set(SECURE_STORE_KEYS.pendingRef, serialized);
  return true;
}

export async function captureInitialReferral(): Promise<boolean> {
  const url = await Linking.getInitialURL();
  return captureReferralFromUrl(url);
}

export function subscribeToReferralLinks(): () => void {
  const subscription = Linking.addEventListener('url', ({ url }) => {
    void captureReferralFromUrl(url).catch((err) => {
      if (__DEV__) console.warn('[referral] failed to capture inbound referral', err);
    });
  });

  return () => subscription.remove();
}

export async function getPendingReferral(): Promise<PendingReferral | null> {
  const value = await secureStore.get(SECURE_STORE_KEYS.pendingRef);
  return parseStoredPendingReferral(value);
}

export function clearPendingReferral(): Promise<void> {
  return secureStore.remove(SECURE_STORE_KEYS.pendingRef);
}

function queryParamsFor(url: string): URLSearchParams {
  try {
    return new URL(url).searchParams;
  } catch {
    const query = url.split('?')[1]?.split('#')[0] ?? '';
    return new URLSearchParams(query);
  }
}

function cleanParam(value: string | null): string | undefined {
  return value?.trim() || undefined;
}
